import useScrollReveal from '../useScrollReveal';
import { sectionStyles, isColorDark, getAnimation, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionDivider from './SectionDivider';

export default function ParallaxSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'centered';
    const overlay = config.overlay_opacity ?? 50;

    const sectionButtons = content.buttons && content.buttons.length > 0
        ? content.buttons
        : content.cta_text ? [{ text: content.cta_text, link: content.cta_link || '#contact', style: 'solid', icon: '', bg_color: '', text_color: '' }] : [];

    const folderImages = content.image_folder_id && mediaFolders ? (mediaFolders[content.image_folder_id] || []) : [];
    const bgImage = folderImages[0]?.image_url || content.image_url || config.bg_image_url || null;

    return (
        <section className="relative overflow-hidden" style={{ minHeight: '400px' }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            {/* Parallax background */}
            {bgImage && (
                <div className="absolute inset-0" style={{ transform: 'translateZ(0)' }}>
                    <img src={bgImage} alt="" className="w-full h-full object-cover" style={{ transform: 'scale(1.1)' }} loading="lazy" />
                </div>
            )}
            {/* Overlay */}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${overlay / 100})` }} />
            {/* Gradient overlay for non-image */}
            {!bgImage && (
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${config.bg_color || s.accent}ee, ${config.bg_gradient_to || s.accent}ee)` }} />
            )}

            {/* Content */}
            <div ref={ref} className={`relative z-10 flex items-center justify-center min-h-[400px] px-4 sm:px-6 lg:px-8 py-20 ${anim}`}>
                <div className={`max-w-3xl mx-auto ${variant === 'left' ? 'text-left' : 'text-center'}`}>
                    {content.title && (
                        <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                            {content.title}
                        </h2>
                    )}
                    {content.subtitle && (
                        <p className={`text-lg md:text-xl text-white/80 mt-4 leading-relaxed transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                            {content.subtitle}
                        </p>
                    )}
                    {sectionButtons.length > 0 && (
                        <div className={`mt-8 flex flex-wrap ${variant === 'left' ? 'justify-start' : 'justify-center'} gap-3 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                            {sectionButtons.map((btn, i) => {
                                const btnStyle = btn.style || 'solid';
                                const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                                const lp = resolveLinkProps(btn.link || '#');
                                const customBg = btn.bg_color;
                                const customText = btn.text_color;
                                let cls = 'px-8 py-4 rounded-lg font-bold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                                let style = {};
                                if (btnStyle === 'solid' || btnStyle === 'pill') {
                                    if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg hover:shadow-2xl'; }
                                    else { style.backgroundColor = s.accent; style.color = '#ffffff'; cls += 'shadow-lg hover:shadow-2xl'; }
                                } else if (btnStyle === 'outline') {
                                    if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                                    else { style.color = '#ffffff'; style.borderColor = '#ffffff'; cls += 'border-2 hover:opacity-80'; }
                                } else { style.color = customText || '#ffffff'; cls += 'hover:opacity-80'; }
                                if (btnStyle === 'pill') cls = cls.replace('rounded-lg', 'rounded-full');
                                if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                                return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                            })}
                        </div>
                    )}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
